import { useState } from 'react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { MapPin, Mail, Phone, Clock, Send, ShieldCheck, BadgeCheck, Lock, AlertCircle, CheckCircle2, MessageSquare, ChevronDown } from 'lucide-react';

const faqs = [
  { q: 'Will you charge me any fee for applying?', a: 'No. Our service is 100% free for borrowers. We are compensated by the lender — not by you. You will never be asked to pay any advisory or referral fee.' },
  { q: 'Is my personal data safe when I apply?', a: 'Yes. All data is transmitted using 256-bit SSL encryption and stored in RBI-compliant servers. Your information is shared only with the lenders you specifically consent to.' },
  { q: 'How do you make sure I get the best rate?', a: 'We compare your profile against 50+ lenders in real-time, negotiate directly with bank relationship managers, and present you the top 3 options with full cost breakdowns — not just EMI figures.' },
  { q: 'What if a lender charges me something unexpected?', a: 'Call our grievance helpline immediately. We escalate to the lender\'s nodal officer within 24 hours and, if unresolved, to the RBI Banking Ombudsman on your behalf.' },
  { q: 'How long does approval take?', a: 'In-principle sanction typically takes 24–48 hours after document submission. Final disbursement can happen within 3–7 working days depending on the lender.' },
];

const safetyPromises = [
  { icon: Lock, title: 'Encrypted Data', desc: '256-bit SSL. Your documents are never stored on our servers — only forwarded to RBI-licensed lenders with your consent.' },
  { icon: ShieldCheck, title: 'No Hidden Fees', desc: 'We charge borrowers nothing. Every cost involved in your loan is disclosed to you upfront before you proceed.' },
  { icon: BadgeCheck, title: 'Verified Lenders Only', desc: 'Every bank and NBFC in our network is RBI-registered. No unregulated lenders, no predatory products.' },
];

const FaqAccordion = () => {
  const [openIndex, setOpenIndex] = useState(null);
  const toggle = (i) => setOpenIndex(openIndex === i ? null : i);

  return (
    <div className="mb-12">
      <div className="text-center mb-12">
        <span className="inline-block bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Common Questions</span>
        <h2 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] mb-4">Frequently Asked Questions</h2>
        <p className="text-muted-foreground max-w-xl mx-auto">Answers to the most common questions about costs, safety, and our process.</p>
      </div>
      <div className="space-y-3 max-w-3xl mx-auto">
        {faqs.map((faq, i) => {
          const isOpen = openIndex === i;
          return (
            <div
              key={i}
              className={`bg-white dark:bg-slate-800 rounded-2xl border transition-all duration-200 overflow-hidden ${isOpen ? 'border-indigo-300 dark:border-indigo-700 shadow-md' : 'border-slate-100 dark:border-slate-700 hover:shadow-sm'}`}
            >
              <button
                onClick={() => toggle(i)}
                className="w-full flex items-center gap-4 px-6 py-5 text-left group focus:outline-none"
                aria-expanded={isOpen}
              >
                <div className={`h-8 w-8 rounded-xl flex items-center justify-center shrink-0 transition-colors ${isOpen ? 'bg-indigo-500' : 'bg-indigo-100 dark:bg-indigo-900/30 group-hover:bg-indigo-200 dark:group-hover:bg-indigo-800/40'}`}>
                  <MessageSquare className={`h-4 w-4 transition-colors ${isOpen ? 'text-white' : 'text-indigo-600 dark:text-indigo-400'}`} />
                </div>
                <span className="flex-1 font-bold text-slate-900 dark:text-white text-sm leading-snug">
                  {faq.q}
                </span>
                <ChevronDown
                  className={`h-5 w-5 shrink-0 text-slate-400 transition-transform duration-300 ${isOpen ? 'rotate-180 text-indigo-500' : ''}`}
                />
              </button>
              <div
                style={{ maxHeight: isOpen ? '300px' : '0px', opacity: isOpen ? 1 : 0 }}
                className="transition-all duration-300 ease-in-out overflow-hidden"
              >
                <div className="px-6 pb-5 pl-[4.5rem]">
                  <p className="text-sm text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Contact = () => {
  return (
    <div className="min-h-screen bg-background b-24">

      {/* ── Hero ── */}
      <div className="bg-slate-900 text-white py-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-sky-950 opacity-80" />
        <div className="relative z-10 container mx-auto px-4">
          <span className="inline-block bg-sky-500/15 text-sky-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-6">We're Here For You</span>
          <h1 className="text-4xl md:text-5xl font-bold font-['Plus_Jakarta_Sans'] mb-4">Contact Us</h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto">Have a question or need assistance? Our team is available 6 days a week to help you navigate your loan journey safely.</p>
        </div>
      </div>

      <div className="container mx-auto px-4 mt-16 max-w-6xl">

        {/* ── Contact Grid ── */}
        <div className="grid lg:grid-cols-3 gap-12 mb-24">
          <div className="lg:col-span-1 space-y-8">
            <div>
              <h2 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] mb-3">Get in Touch</h2>
              <p className="text-muted-foreground">Reach out to our support team for loan queries, agent onboarding, and general assistance.</p>
            </div>
            <div className="space-y-6">
              {[
                { icon: MapPin, title: 'Corporate Office', lines: ['123 Finance Street, Bandra Kurla Complex,', 'Mumbai, India 400051'] },
                { icon: Phone, title: 'Phone Support', lines: ['Toll Free: 1800-XXX-XXXX', 'Direct: +91 98765 43210'] },
                { icon: Mail, title: 'Email Us', lines: ['support@FinMatrixRQ.com', 'partners@FinMatrixRQ.com'] },
                { icon: Clock, title: 'Working Hours', lines: ['Monday - Saturday', '9:00 AM to 7:00 PM (IST)'] },
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center shrink-0">
                    <item.icon className="h-5 w-5 text-emerald-600 dark:text-emerald-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold">{item.title}</h4>
                    <p className="text-muted-foreground text-sm mt-1">{item.lines[0]}<br />{item.lines[1]}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2">
            <Card className="border-0 shadow-2xl bg-white dark:bg-slate-800">
              <CardHeader className="p-8 pb-4">
                <CardTitle className="text-2xl font-bold font-['Plus_Jakarta_Sans']">Send us a Message</CardTitle>
                <CardDescription>We typically respond within 2 hours during working hours.</CardDescription>
              </CardHeader>
              <CardContent className="p-8 pt-0">
                <form className="space-y-6" onSubmit={(e) => e.preventDefault()}>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2"><Label htmlFor="name">Your Name</Label><Input id="name" placeholder="John Doe" required /></div>
                    <div className="space-y-2"><Label htmlFor="email">Email Address</Label><Input id="email" type="email" placeholder="john@example.com" required /></div>
                  </div>
                  <div className="space-y-2"><Label htmlFor="subject">Subject</Label><Input id="subject" placeholder="How can we help you?" required /></div>
                  <div className="space-y-2"><Label htmlFor="message">Your Message</Label><Textarea id="message" placeholder="Type your message here..." className="resize-none" rows={6} required /></div>
                  <Button type="submit" size="lg" className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 text-white font-semibold">
                    <Send className="mr-2 h-4 w-4" /> Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* ── NEW: Our Safety Promises ── */}
        <div className="mb-24">
          <div className="text-center mb-12">
            <span className="inline-block bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 text-xs font-semibold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">Before You Share Anything</span>
            <h2 className="text-3xl font-bold font-['Plus_Jakarta_Sans'] mb-4">Your safety is our first priority</h2>
            <p className="text-muted-foreground max-w-xl mx-auto">Whether you're making an inquiry or submitting documents — here's exactly how we keep you protected.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {safetyPromises.map((s, i) => (
              <div key={i} className="group bg-white dark:bg-slate-800 rounded-2xl border border-slate-100 dark:border-slate-700 p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="h-14 w-14 rounded-2xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mb-6 group-hover:bg-emerald-500 transition-colors">
                  <s.icon className="h-7 w-7 text-emerald-600 dark:text-emerald-400 group-hover:text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 font-['Plus_Jakarta_Sans']">{s.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ── NEW: FAQs (Accordion) ── */}
        <FaqAccordion />

        {/* ── NEW: Reassurance stripe ── */}
        <div className="rounded-3xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white p-10 my-24 text-center">
          <AlertCircle className="h-8 w-8 mx-auto mb-4 text-emerald-200" />
          <h2 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] mb-3">Something feel off? Report it immediately.</h2>
          <p className="text-emerald-100 max-w-xl mx-auto mb-6 text-sm">If any lender or agent associated with FinMatrixRQ asks you for cash, additional undisclosed fees, or behaves unethically — report it to us right away. We take every complaint seriously.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3">
              <Phone className="h-4 w-4 text-emerald-200" />
              <span className="font-semibold text-sm">Grievance: 1800-XXX-XXXX</span>
            </div>
            <div className="flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-xl px-5 py-3">
              <Mail className="h-4 w-4 text-emerald-200" />
              <span className="font-semibold text-sm">grievance@FinMatrixRQ.com</span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

export default Contact;
// Tue Feb 24 17:34:19 IST 2026
